import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Download, Trash2, CreditCard, FileText, AlertTriangle } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
}
function fmtMoney(cents, currency) {
  if (cents == null) return '—';
  const v = (cents / 100).toFixed(2);
  return `${(currency || 'usd').toUpperCase()} ${v}`;
}

export default function AccountDataPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [confirm, setConfirm] = useState('');

  const [billing, setBilling] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesError, setInvoicesError] = useState('');
  const [auraCount, setAuraCount] = useState(0);
  const [targetPlan, setTargetPlan] = useState('');
  const [pendingPreview, setPendingPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [b, a] = await Promise.all([
        axios.get(`${API_URL}/api/account/billing`, { withCredentials: true }),
        axios.get(`${API_URL}/api/auras`, { withCredentials: true }).catch(() => ({ data: { auras: [] } })),
      ]);
      setBilling(b.data);
      const auras = b.data?.auras || a.data?.auras || a.data || [];
      const count = Array.isArray(auras) ? auras.length : (a.data?.auras?.length || 0);
      setAuraCount(count);
    } catch (_) { /* ignore */ }
    try {
      const inv = await axios.get(`${API_URL}/api/account/invoices`, { withCredentials: true });
      setInvoices(inv.data.invoices || []);
      setInvoicesError('');
    } catch (e) {
      setInvoicesError(e.response?.status === 503 ? 'Invoices unavailable (Stripe not configured).' : '');
    }
  }, []);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  const planRank = (key) => {
    const p = billing?.plans?.[key];
    return p ? Number(p.price_monthly || 0) : 0;
  };
  const currentKey = billing?.current_plan || 'free';
  const lowerPlans = Object.keys(billing?.plans || {}).filter(k => planRank(k) < planRank(currentKey));

  const onSelectPlan = (k) => {
    setTargetPlan(k);
    if (!k) { setPendingPreview(null); return; }
    const newMax = billing?.plans?.[k]?.max_auras ?? 0;
    const excess = (newMax === -1 || newMax < 0) ? 0 : Math.max(0, auraCount - newMax);
    const effective = billing?.sub?.current_period_end || billing?.pending_change?.effective_at || null;
    setPendingPreview({ to: k, effective_at: effective, excess, newMax });
  };

  const confirmDowngrade = async () => {
    if (!targetPlan) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/account/downgrade`, { to_plan: targetPlan }, { withCredentials: true });
      alert(`Downgrade scheduled. Effective ${fmtDate(data.effective_at)}. ${data.excess_auras} event(s) will be archived.`);
      setTargetPlan(''); setPendingPreview(null);
      await loadAll();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const cancelDowngrade = async () => {
    if (!window.confirm('Cancel the scheduled downgrade?')) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/account/downgrade/cancel`, {}, { withCredentials: true });
      await loadAll();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const requestExport = async () => {
    setExporting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/account/export`, {}, { withCredentials: true });
      alert(`Export queued (job ${data.job_id}). You'll receive an email when ready.`);
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setExporting(false); }
  };

  const requestErase = async () => {
    if (confirm !== 'ERASE') return alert('Type ERASE exactly.');
    if (!window.confirm('This is irreversible. Continue?')) return;
    setErasing(true);
    try {
      await axios.post(`${API_URL}/api/account/erase`, { confirm: 'ERASE' }, { withCredentials: true });
      alert('Your account has been erased.');
      await logout();
      navigate('/');
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setErasing(false); }
  };

  if (!user) return null;

  const pending = billing?.pending_change;
  const currentPlanName = billing?.plans?.[currentKey]?.name || currentKey;
  const currentPlanPrice = billing?.plans?.[currentKey]?.price_monthly;

  return (
    <div className="min-h-screen bg-midnight">
      <SiteHeader />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6">

        {/* Plan & Billing */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <h2 className="text-text-light text-lg font-semibold mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-champagne" /> Plan & Billing
          </h2>

          <div className="text-sm text-text-muted mb-4">
            <div>Current plan: <span className="text-text-light font-medium">{currentPlanName}</span>
              {currentPlanPrice != null && <span className="ml-2 text-champagne">${currentPlanPrice}/mo</span>}
            </div>
            {billing?.sub?.current_period_end && (
              <div className="mt-1">Billing period ends: <span className="text-text-light">{fmtDate(billing.sub.current_period_end)}</span></div>
            )}
            {!billing?.sub && billing?.effective_plan && billing.effective_plan !== currentKey && (
              <div className="mt-1">Active entitlement (grant): <span className="text-text-light">{billing.effective_plan}</span></div>
            )}
          </div>

          {pending && (
            <div className="mb-4 rounded-lg border border-champagne/30 bg-champagne/5 p-3 text-sm" data-testid="downgrade-banner">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-champagne mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-text-light">
                    Downgrade scheduled to <strong>{billing?.plans?.[pending.to_plan]?.name || pending.to_plan}</strong> on {fmtDate(pending.effective_at)}.
                  </div>
                  <button onClick={cancelDowngrade} disabled={busy}
                    className="mt-2 px-3 py-1 rounded bg-white/10 hover:bg-white/20 text-text-light text-xs disabled:opacity-30"
                    data-testid="cancel-downgrade-button">
                    Cancel downgrade
                  </button>
                </div>
              </div>
            </div>
          )}

          {!pending && lowerPlans.length > 0 && (
            <div className="space-y-3">
              <label className="block text-xs text-text-muted">Change plan (downgrade only):</label>
              <select value={targetPlan} onChange={e => onSelectPlan(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-text-light text-sm"
                data-testid="downgrade-select">
                <option value="">— select a lower plan —</option>
                {lowerPlans.map(k => (
                  <option key={k} value={k}>{billing.plans[k].name} (${billing.plans[k].price_monthly}/mo)</option>
                ))}
              </select>

              {pendingPreview && targetPlan && (
                <div className="rounded-lg border border-white/10 bg-midnight/40 p-3 text-sm text-text-muted" data-testid="downgrade-confirm">
                  <div className="text-text-light mb-2">
                    Switching from <strong>{currentPlanName}</strong> to <strong>{billing.plans[targetPlan].name}</strong>.
                  </div>
                  {pendingPreview.effective_at && (
                    <div>Effective on <strong className="text-text-light">{fmtDate(pendingPreview.effective_at)}</strong> (end of current billing period).</div>
                  )}
                  <div className="mt-2">
                    You currently host {auraCount} event(s).
                    {pendingPreview.newMax !== -1 && <> {billing.plans[targetPlan].name} allows {pendingPreview.newMax}.</>}
                  </div>
                  {pendingPreview.excess > 0 && (
                    <div className="mt-1 text-champagne">
                      <strong>{pendingPreview.excess} oldest event(s) will be archived (read-only).</strong>
                    </div>
                  )}
                  <div className="mt-3 flex gap-2">
                    <button onClick={confirmDowngrade} disabled={busy}
                      className="px-3 py-2 rounded-lg bg-champagne text-midnight text-sm font-medium hover:bg-champagne-light disabled:opacity-30"
                      data-testid="downgrade-confirm-button">
                      {busy ? 'Scheduling…' : 'Confirm downgrade'}
                    </button>
                    <button onClick={() => onSelectPlan('')} disabled={busy}
                      className="px-3 py-2 rounded-lg bg-white/5 text-text-light text-sm hover:bg-white/10">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Invoices */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <h2 className="text-text-light text-lg font-semibold mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-champagne" /> Invoices
          </h2>
          {invoicesError && <p className="text-text-muted text-sm">{invoicesError}</p>}
          {!invoicesError && invoices.length === 0 && (
            <p className="text-text-muted text-sm">No invoices yet.</p>
          )}
          {invoices.length > 0 && (
            <ul className="divide-y divide-white/5" data-testid="invoice-list">
              {invoices.map(inv => (
                <li key={inv.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="text-text-light truncate">{inv.number || inv.id}</div>
                    <div className="text-text-muted text-xs">
                      {inv.created ? fmtDate(new Date(inv.created * 1000).toISOString()) : ''}
                      <span className="ml-2 capitalize">{inv.status || ''}</span>
                    </div>
                  </div>
                  <div className="text-text-light text-sm whitespace-nowrap">{fmtMoney(inv.amount_paid, inv.currency)}</div>
                  <a href={`${API_URL}/api/account/invoices/${inv.id}/download`}
                    target="_blank" rel="noreferrer"
                    className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-text-light text-xs">
                    PDF
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Download data */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <h2 className="text-text-light text-lg font-semibold mb-2 flex items-center gap-2">
            <Download className="w-5 h-5 text-champagne" /> Download your data
          </h2>
          <p className="text-text-muted text-sm mb-4">
            We'll bundle your account, auras, media metadata, and transactions into a ZIP. You'll get an email with a 7-day download link.
          </p>
          <button onClick={requestExport} disabled={exporting}
            className="px-4 py-2 rounded-lg bg-champagne text-midnight text-sm font-medium hover:bg-champagne-light disabled:opacity-30"
            data-testid="export-button">
            {exporting ? 'Queuing…' : 'Request data export'}
          </button>
        </div>

        <div className="rounded-2xl border border-error/30 bg-error/5 p-6">
          <h2 className="text-error text-lg font-semibold mb-2 flex items-center gap-2">
            <Trash2 className="w-5 h-5" /> Erase your account
          </h2>
          <p className="text-text-muted text-sm mb-4">
            This is permanent. Your auras and media will be deleted. Payment records are kept for accounting compliance but anonymized.
          </p>
          <label className="block text-xs text-text-muted mb-1">Type ERASE to confirm:</label>
          <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="ERASE"
            className="w-full px-3 py-2 mb-3 rounded-lg bg-white/5 border border-white/10 text-text-light text-sm" />
          <button onClick={requestErase} disabled={erasing || confirm !== 'ERASE'}
            className="px-4 py-2 rounded-lg bg-error text-white text-sm font-medium hover:bg-error/80 disabled:opacity-30"
            data-testid="erase-button">
            {erasing ? 'Erasing…' : 'Erase my account'}
          </button>
        </div>
      </div>
      <FooterExpanded />
    </div>
  );
}
